import { cn } from '@/lib/utils';

/** The Saudi Riyal sign, U+20C1. */
const RIYAL_SIGN = '⃁';

/**
 * Renders the riyal symbol with an accessible name.
 *
 * The glyph alone is not enough: U+20C1 was encoded in Unicode 17 (Sept 2025),
 * and screen readers that predate it announce nothing at all — a price would be
 * read as a bare number with no currency. So the symbol is hidden from the
 * accessibility tree and the word is supplied beside it, which is what the
 * reader announces and what a copied selection keeps.
 *
 * No `dir` override. Arabic reads right to left, so the number comes first and
 * the currency after it — which is the logical order here, and bidi renders it
 * correctly in both scripts. Forcing `ltr` would flip the pair in RTL and put
 * the sign ahead of the amount.
 */
export const Riyal = ({ className }: { className?: string }) => (
  <span className={cn('whitespace-nowrap', className)}>
    <span aria-hidden="true">{RIYAL_SIGN}</span>
    <span className="sr-only">ريال</span>
  </span>
);

/** Amount followed by the sign — `<Price amount="٢-٣" />` reads "٢-٣ ريال". */
export const Price = ({
  amount,
  className,
}: {
  amount: string;
  className?: string;
}) => (
  <span className={cn('whitespace-nowrap', className)}>
    {amount}
    <span aria-hidden="true">{' ' + RIYAL_SIGN}</span>
    <span className="sr-only"> ريال</span>
  </span>
);

export default Riyal;
